import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { paths } from "../../app/paths";
import { StatusPage } from "./StatusPage";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort boundary for failures outside the router (e.g. in providers). Uses no router
 * APIs, so it still renders when the router is the thing that broke.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Central error reporting is wired in BATCH 14 (observability). Never log PHI here.
    console.error("ThyroAID unhandled UI error", error.name, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <StatusPage
        code="Something went wrong"
        title="ThyroAID stopped unexpectedly"
        action={{ href: paths.home, label: "Reload ThyroAID" }}
        {...(import.meta.env.DEV ? { details: `${error.name}: ${error.message}` } : {})}
      >
        <p>Reload to continue. If this keeps happening, contact support.</p>
      </StatusPage>
    );
  }
}
